import React from 'react';
import { Nav } from 'react-bootstrap';
import { NavSectionHeader } from './navMenuLayout';
import ProjectsNavSection from './ProjectsNavSection';
import TendersNavSection, { isTenderWorkspaceRoute } from './TendersNavSection';
import AdministrationNavSection from './AdministrationNavSection';
import HrNavSection from './HrNavSection';
import SurveyNavSection from './SurveyNavSection';
import DrillingNavSection from './DrillingNavSection';
import RecordingNavSection from './RecordingNavSection';
import BaseCampNavSection from './BaseCampNavSection';
import OperationsNavSection from './OperationsNavSection';
import QhseNavSection from './QhseNavSection';
import SettingsHelpNavSection from './SettingsHelpNavSection';
import { isJmOperatorProfile } from '../../../utils/accessProfiles';

/**
 * Desktop sidebar menu tree (company, project, settings groups).
 * Extracted from NavigationBar so the shell only handles layout, project selector and chrome.
 */
export default function DesktopSidebarMenus({
  collapsed,
  MenuItem,
  SubMenuItem,
  SubMenuGroup,
  ActionMenuItem,
  closeMobileSidebar,
  hasMenuAccess,
  hasMenuAccessOrChild,
  showCompanyMenu,
  hasProjects,
  activeProject,
  hasApprovedBudget,
  approvedPresence,
  isAdmin,
  isSuperAdmin,
  isPlatformDeveloper,
  isDemoProjectBootstrap,
  hasSurvey,
  hasDrilling,
  hasRecording,
  hasBaseCamp,
  showHelicopterOpsMenu,
  online,
  menusLoaded,
  user,
  onNewProject,
  onContactSupport,
  onAboutOpen,
  accessManifest,
  TopLevelActionItem,
  pathname,
  onExitTenderWorkspace,
}) {
  const isJmOperatorOnly = isJmOperatorProfile(accessManifest);
  const tenderAccess = accessManifest?.tender;

  // Bidding and operating do not mix: while you are inside a tender the sidebar
  // is that workspace and nothing else. Driven by the route, not by a mode you
  // switch on — a mode you can forget you are in is a trap, and here leaving is
  // just navigating out.
  const inTenderWorkspace = Boolean(
    tenderAccess?.hasAccess && isTenderWorkspaceRoute(pathname),
  );
  const menuPrimitives = {
    MenuItem,
    SubMenuItem,
    SubMenuGroup,
    ActionMenuItem,
    hasMenuAccess,
    hasMenuAccessOrChild,
    closeMobileSidebar,
  };

  const showSettingsGroup = !isJmOperatorOnly && (
    isDemoProjectBootstrap
    || hasMenuAccessOrChild('/settings', ['/master-data/clients', '/master-data/departments', '/master-data/roles', '/master-data/equipment-categories', '/master-data/equipment-items', '/admin/users', '/access-control', '/settings/company', '/settings/currencies', '/settings/display'])
    || isAdmin
    || isSuperAdmin
    || hasMenuAccessOrChild('/help', ['/help/manual'])
  );

  if (isJmOperatorOnly) {
    return (
      <Nav className="flex-column mb-auto w-100 pt-2" data-tour="nav-menu">
        <NavSectionHeader collapsed={collapsed} label="Company" variant="company" />

        <ProjectsNavSection
          {...menuPrimitives}
          hasProjects={hasProjects}
          activeProject={activeProject}
          hasApprovedBudget={hasApprovedBudget}
          onNewProject={onNewProject}
          hideNewProject
        />

        {activeProject && (
          <>
            <NavSectionHeader collapsed={collapsed} label="Project" variant="project" className="mt-2" />
            <OperationsNavSection
              {...menuPrimitives}
              showHelicopterOpsMenu={false}
              online={online}
            />
          </>
        )}

        {hasMenuAccessOrChild('/help', ['/help/manual']) && (
          <>
            <NavSectionHeader collapsed={collapsed} label="Settings" variant="settings" />
            <SettingsHelpNavSection
              {...menuPrimitives}
              isDemoProjectBootstrap={false}
              isSuperAdmin={false}
              isPlatformDeveloper={false}
              isAdmin={false}
              menusLoaded={menusLoaded}
              user={user}
              onContactSupport={onContactSupport}
              onAboutOpen={onAboutOpen}
            />
          </>
        )}
      </Nav>
    );
  }

  // Checked after the JM profile so that narrowing is never widened by walking
  // into a tender.
  if (inTenderWorkspace) {
    return (
      <Nav className="flex-column mb-auto w-100 pt-2" data-tour="nav-menu">
        <NavSectionHeader collapsed={collapsed} label="Tender" variant="tender" />

        <TendersNavSection {...menuPrimitives} tenderAccess={tenderAccess} />

        <NavSectionHeader collapsed={collapsed} label="Company" variant="company" className="mt-2" />

        {/* The way back. Without it the only exits are the browser's back button
            and the logo, and neither says what it does. */}
        <TopLevelActionItem
          icon="bi-box-arrow-left"
          title="Exit to operations"
          onClick={onExitTenderWorkspace}
        />

        {hasMenuAccessOrChild('/help', ['/help/manual']) && (
          <SettingsHelpNavSection
            {...menuPrimitives}
            isDemoProjectBootstrap={false}
            isSuperAdmin={isSuperAdmin}
            isPlatformDeveloper={isPlatformDeveloper}
            isAdmin={isAdmin}
            menusLoaded={menusLoaded}
            user={user}
            onContactSupport={onContactSupport}
            onAboutOpen={onAboutOpen}
          />
        )}
      </Nav>
    );
  }

  return (
    <Nav className="flex-column mb-auto w-100 pt-2" data-tour="nav-menu">
      <NavSectionHeader collapsed={collapsed} label="Company" variant="company" />

      {/* Only for people who actually hold a live grant on some tender — the
          manifest carries that, since it is not expressible as a route
          permission. */}
      <TendersNavSection {...menuPrimitives} tenderAccess={accessManifest?.tender} />

      <ProjectsNavSection
        {...menuPrimitives}
        hasProjects={hasProjects}
        activeProject={activeProject}
        hasApprovedBudget={hasApprovedBudget}
        onNewProject={onNewProject}
      />

      <AdministrationNavSection
        {...menuPrimitives}
        showCompanyMenu={showCompanyMenu}
        isAdmin={isAdmin}
        activeProject={activeProject}
        hasApprovedBudget={hasApprovedBudget}
      />

      {showCompanyMenu('hr') && (
        <HrNavSection {...menuPrimitives} activeProject={activeProject} />
      )}

      {activeProject && <NavSectionHeader collapsed={collapsed} label="Project" variant="project" className="mt-2" />}

      {hasSurvey && <SurveyNavSection {...menuPrimitives} />}

      {hasDrilling && (
        <DrillingNavSection {...menuPrimitives} approvedPresence={approvedPresence} />
      )}

      {hasRecording && <RecordingNavSection {...menuPrimitives} />}

      {hasBaseCamp && <BaseCampNavSection {...menuPrimitives} />}

      {activeProject && (
        <OperationsNavSection
          {...menuPrimitives}
          showHelicopterOpsMenu={showHelicopterOpsMenu}
          online={online}
        />
      )}

      {activeProject && <QhseNavSection {...menuPrimitives} />}

      {showSettingsGroup && (
        <>
          <NavSectionHeader collapsed={collapsed} label="Settings" variant="settings" />
          <SettingsHelpNavSection
            {...menuPrimitives}
            isDemoProjectBootstrap={isDemoProjectBootstrap}
            isSuperAdmin={isSuperAdmin}
            isPlatformDeveloper={isPlatformDeveloper}
            isAdmin={isAdmin}
            menusLoaded={menusLoaded}
            user={user}
            onContactSupport={onContactSupport}
            onAboutOpen={onAboutOpen}
          />
        </>
      )}
    </Nav>
  );
}
